import { NavLink } from 'react-router-dom'
import { ROUTES } from '@/shared/lib/routes'
import { Trans } from '@lingui/react/macro'
import { t } from '@lingui/core/macro'
import { OrgSwitcher } from '@/features/auth/components/OrgSwitcher'
import { LocaleSwitcher } from '@/shared/components/LocaleSwitcher'
import { useScrollUI } from '@/shared/hooks/useScrollUI'
import { useLogout } from '@/features/auth/hooks/useAuth'
import { cn } from '@/lib/utils'

const iconProps = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true as const,
}

function LogOutIcon() {
  return (
    <svg {...iconProps} width="18" height="18">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  )
}

function PlusCircleIcon() {
  return (
    <svg {...iconProps} width="24" height="24">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12h8" />
      <path d="M12 8v8" />
    </svg>
  )
}

function ClipboardIcon() {
  return (
    <svg {...iconProps} width="24" height="24">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
    </svg>
  )
}

function NavTab({
  to,
  end,
  icon,
  children,
}: {
  to: string
  end?: boolean
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex flex-1 flex-col items-center justify-center gap-1 py-3 text-xs font-medium',
          isActive ? 'text-primary' : 'text-muted-foreground',
        )
      }
    >
      {icon}
      {children}
    </NavLink>
  )
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const logout = useLogout()
  const { isUIHidden, isKeyboardOpen } = useScrollUI()
  const hideBottomNav = isUIHidden || isKeyboardOpen

  return (
    <div className="sm:border-border mx-auto flex min-h-svh w-full max-w-xl flex-col sm:border-x">
      <a
        href="#main-content"
        className="focus:bg-primary focus:text-primary-foreground sr-only focus:not-sr-only focus:absolute focus:z-50 focus:px-4 focus:py-2"
      >
        <Trans>Skip to content</Trans>
      </a>

      <header
        className={cn(
          'border-border bg-background/95 supports-backdrop-filter:bg-background/85 fixed top-0 right-0 left-0 z-50 mx-auto flex max-w-xl items-center gap-2 border-b px-4 pt-[calc(env(safe-area-inset-top)+0.625rem)] pb-2.5 backdrop-blur transition-transform duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
          isUIHidden
            ? 'pointer-events-none -translate-y-[calc(100%+env(safe-area-inset-top))]'
            : 'translate-y-0',
        )}
      >
        <img
          src="/icon-192x192.png"
          alt="Fiduly"
          className="h-9 w-9 shrink-0 sm:hidden"
        />
        <img
          src="/logo-fiduly.png"
          alt="Fiduly"
          className="hidden h-8 shrink-0 sm:block"
        />
        <div className="min-w-0 flex-1" />
        <LocaleSwitcher persistToServer />
        <OrgSwitcher />
        <button
          onClick={() => {
            if (window.confirm(t`Log out?`)) logout.mutate()
          }}
          className="text-muted-foreground hover:bg-muted hover:text-foreground flex h-11 w-11 shrink-0 items-center justify-center rounded-md"
          aria-label={t`Log out`}
        >
          <LogOutIcon />
        </button>
      </header>

      <main
        id="main-content"
        className="flex-1 pt-[calc(env(safe-area-inset-top)+4rem)] pb-[calc(56px+env(safe-area-inset-bottom))]"
      >
        {children}
      </main>

      <nav
        aria-label={t`Main navigation`}
        className={cn(
          'border-border bg-background/98 supports-backdrop-filter:bg-background/88 fixed right-0 bottom-0 left-0 z-40 mx-auto flex min-h-[56px] max-w-xl border-t pb-[env(safe-area-inset-bottom)] backdrop-blur transition-transform duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
          hideBottomNav
            ? 'pointer-events-none translate-y-[calc(100%+env(safe-area-inset-bottom))]'
            : 'translate-y-0',
        )}
      >
        <NavTab to={ROUTES.home} end icon={<PlusCircleIcon />}>
          <Trans>Add</Trans>
        </NavTab>
        <NavTab to={ROUTES.expenses} icon={<ClipboardIcon />}>
          <Trans>List</Trans>
        </NavTab>
      </nav>
    </div>
  )
}
